// Generic optimistic-cache helper that handles BOTH cache shapes registered
// at a prefix — `InfiniteData<P>` from useInfiniteQuery (wraps pages[]) and
// flat `P` from useQuery (a single page-like value) — in one call. Existing
// hand-rolled patchers in feed.ts / notifications.ts / spaces.ts / resources.ts
// reimplement this dispatch loop; consolidate into one utility.
//
// Why this exists alongside `query-cache-utils.ts`:
//
// The helpers in `query-cache-utils.ts` operate on a list's `items` array
// directly — they assume a `{ items: T[] }` page shape with top-level `.id`
// items. Several feeds violate that assumption: feed pages hold a tagged
// union (`FeedItemView`) where only `kind: 'post'` items carry an id under
// `.post.id`; AuthoredPostView arrays are flat `readonly T[]` (no `.items`);
// notification pages need a "changed somewhere?" signal so the badge-decrement
// doesn't fire when the cache had no matching unread row.
//
// This helper supports the general case: the caller provides a `transform`
// that receives the *whole* cached value (page or infinite-data) and returns
// the next value (or `null` to leave the entry untouched). The helper
// captures snapshots, runs the transform, writes the result, and returns
// the bag for `restoreSnapshots` (re-exported from `query-cache-utils`).

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { SnapshotSet } from '../query-cache-utils';

type Entry = readonly [readonly unknown[], unknown];

export type TransformResult<V> = V | null;

/**
 * Walk every cached value whose key starts with `prefix`, run `transform`,
 * and write the result. Returns snapshots for rollback.
 *
 * `transform` returning `null` (or returning the same reference) leaves the
 * cache entry alone — snapshot is NOT captured for those. Returning a new
 * value writes it and snapshots the previous value.
 *
 * The optional `predicate` further filters which cached keys are visited.
 */
export function transformAllListCaches<V>(
  qc: QueryClient,
  prefix: QueryKey,
  transform: (value: V, key: readonly unknown[]) => TransformResult<V>,
  predicate?: (key: readonly unknown[]) => boolean,
): SnapshotSet {
  const entries = qc.getQueriesData<V>({ queryKey: prefix });
  const previousLists: Entry[] = [];
  for (const [key, value] of entries) {
    if (value === undefined || value === null) continue;
    if (predicate !== undefined && !predicate(key)) continue;
    const next = transform(value, key);
    if (next === null || next === value) continue;
    previousLists.push([key, value]);
    qc.setQueryData<V>(key, next);
  }
  return { previousLists };
}
